import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {CommonThemeColors} from "crqlar-react-common/dist/components/theme";
import {AvailabilityType} from "crqlar-react-common/dist/models/availability/availability.model";
import type {
    AvailabilityCalculationResult
} from "crqlar-react-common/dist/models/availability/availabilityCalculationResult.model";
import {AvailabilityCalculationService} from "crqlar-react-common/dist/services/availability/availabilityCalculation.service";
import {endOfDay, formatDateHM, startOfDay} from "crqlar-react-common/dist/utils/date.util";
import {AVAILABILITY_QUERY_KEY} from "crqlar-react-common/dist/utils/hooks/reservations/calendar/calendar.util";
import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useQuery} from "react-query";
import {useFloorplanContext} from "../hooks/useFloorplanContext";
import {useTimeframe} from "../hooks/useTimeframe";

interface DaySection {
    key: string;
    type: AvailabilityType;
    name?: string;
    timeLabel: string;
    start: number;
}

// Buffer / preparation results are generated around single reservations, not restaurant-wide
const isRestaurantAvailability = (result: AvailabilityCalculationResult) => (
    !result.hidden
    && !result.bufferTimeInfo
    && !result.preparationTimeInfo
    && !result.connectedReservationId
);

/**
 * The selected restaurant's availability sections and block periods on the selected day, as
 * plain HH:mm ranges. Tables have no availability of their own, so the restaurant's is what applies.
 */
const DayAvailability: React.FC = () => {
    const {t} = useTranslation();
    const {restaurantId} = useFloorplanContext();
    const {date} = useTimeframe();

    const dayStart = startOfDay(date);
    const dayEnd = endOfDay(date);

    const {data: results = [], isLoading} = useQuery(
        [AVAILABILITY_QUERY_KEY, "floorplan-day", restaurantId, dayStart.getTime()],
        () => AvailabilityCalculationService.calculate({
            ids: [restaurantId],
            range: {start: dayStart, end: dayEnd},
        }),
        {enabled: !!restaurantId, keepPreviousData: true},
    );

    const sections = useMemo<DaySection[]>(() => {
        const seen = new Set<string>();

        return results
            .filter(result => result.entityId === restaurantId && isRestaurantAvailability(result))
            .flatMap(result => {
                if (!result.startTime || !result.endTime) return [];
                const start = new Date(result.startTime);
                const end = new Date(result.endTime);
                if (end <= dayStart || start > dayEnd) return [];

                // Clip to the selected day; a section running past midnight ends at 24:00
                const shownStart = start < dayStart ? dayStart : start;
                const endLabel = end > dayEnd ? "24:00" : formatDateHM(end);
                const type = result.type === AvailabilityType.CLOSED ? AvailabilityType.CLOSED : AvailabilityType.OPEN;
                const key = `${type}-${result.availabilityId ?? result.id}-${shownStart.getTime()}`;
                if (seen.has(key)) return [];
                seen.add(key);

                return [{
                    key,
                    type,
                    name: result.name,
                    timeLabel: `${formatDateHM(shownStart)} - ${endLabel}`,
                    start: shownStart.getTime(),
                }];
            })
            .sort((a, b) => a.start - b.start);
    }, [results, restaurantId, dayStart.getTime(), dayEnd.getTime()]);

    const openSections = sections.filter(section => section.type === AvailabilityType.OPEN);
    const blockSections = sections.filter(section => section.type === AvailabilityType.CLOSED);

    const renderGroup = (title: string, items: DaySection[], color: string) => (
        <Stack gap={0.25}>
            <Typography sx={{fontSize: 11, fontWeight: 600, color: "text.secondary", lineHeight: 1.4}}>
                {title}
            </Typography>
            {items.map(section => (
                <Stack key={section.key} direction="row" alignItems="center" gap={0.75} sx={{minWidth: 0}}>
                    <Box sx={{width: 8, height: 8, borderRadius: "50%", bgcolor: color, flexShrink: 0}}/>
                    <Typography
                        sx={{
                            fontSize: {xs: 11, sm: 12},
                            fontWeight: 600,
                            fontVariantNumeric: "tabular-nums",
                            whiteSpace: "nowrap",
                            lineHeight: 1.4,
                        }}
                    >
                        {section.timeLabel}
                    </Typography>
                    {section.name && (
                        <Typography
                            sx={{
                                fontSize: {xs: 11, sm: 12},
                                color: "text.secondary",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: 140,
                                lineHeight: 1.4,
                            }}
                        >
                            {section.name}
                        </Typography>
                    )}
                </Stack>
            ))}
        </Stack>
    );

    return (
        <Box
            sx={{
                bgcolor: "background.paper",
                border: 1,
                borderColor: "divider",
                borderRadius: 2,
                px: {xs: 1, sm: 1.25},
                py: 0.75,
                maxHeight: 240,
                overflowY: "auto",
                maxWidth: 280,
            }}
        >
            <Stack gap={0.75}>
                {!isLoading && !sections.length && (
                    <Typography sx={{fontSize: {xs: 11, sm: 12}, color: "text.secondary"}}>
                        {t("No availability this day")}
                    </Typography>
                )}
                {openSections.length > 0 && renderGroup(
                    t("Availability"),
                    openSections,
                    CommonThemeColors.sys.availability.boldBlue,
                )}
                {blockSections.length > 0 && renderGroup(
                    t("Block periods"),
                    blockSections,
                    CommonThemeColors.sys.availability.boldRed,
                )}
            </Stack>
        </Box>
    );
};

export default DayAvailability;
